import { Link } from '@tanstack/react-router';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { useIsDivorceProductEnabled } from '@/hooks/usePublicFeatureFlags';
import { track } from '@/lib/analytics';
import { COMPETITORS } from '@/lib/competitors';

const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/harbor_coparent_app/',
    Icon: FaInstagram,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@harborcoparent',
    Icon: FaTiktok,
  },
] as const;

const COMPARISON_LINKS = COMPETITORS.map((competitor) => ({
  competitor: competitor.slug,
  label: `Harbor vs ${competitor.name}`,
}));

const handleFooterLinkClick = (link: string) => {
  track('Click Footer Link', { link });
};

const linkClassName = 'text-white/70 hover:text-white transition-colors';
const linkStyle = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 400,
} as const;

const sectionHeadingStyle = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 600,
} as const;

export function Footer() {
  const divorceEnabled = useIsDivorceProductEnabled();
  return (
    <footer className='bg-primary-dark py-12'>
      <div className='max-w-7xl mx-auto px-6'>
        <div className='grid grid-cols-2 lg:grid-cols-5 gap-8 mb-8'>
          {/* Brand */}
          <div className='col-span-2 lg:col-span-1'>
            <h3
              className='text-white text-2xl font-light mb-4'
              style={{
                fontFamily:
                  "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              }}
            >
              Harbor
            </h3>
            <p
              className='text-white/70 leading-relaxed mb-6'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
              }}
            >
              Making co-parenting simple, transparent, and focused on what
              matters most: your kids. Available in the US and Canada.
            </p>
            <div className='flex items-center gap-4'>
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label={label}
                  onClick={() => handleFooterLinkClick(label)}
                  className='text-white/70 hover:text-white transition-colors'
                >
                  <Icon className='w-6 h-6' aria-hidden />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className='text-white font-semibold mb-4' style={sectionHeadingStyle}>
              Product
            </h4>
            <ul className='space-y-2'>
              <li>
                <Link
                  to='/features'
                  onClick={() => handleFooterLinkClick('Features')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Features
                </Link>
              </li>
              <li>
                <Link
                  to='/blog'
                  onClick={() => handleFooterLinkClick('Blog')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Blog
                </Link>
              </li>
              <li>
                <a
                  href='/#pricing'
                  onClick={() => handleFooterLinkClick('Pricing')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Pricing
                </a>
              </li>
              <li>
                <Link
                  to='/compare'
                  onClick={() => handleFooterLinkClick('Compare Apps')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Compare Apps
                </Link>
              </li>
              <li>
                <Link
                  to='/learn/coparents'
                  onClick={() => handleFooterLinkClick('For Co-parents')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  For Co-parents
                </Link>
              </li>
              <li>
                <Link
                  to='/learn/practitioners'
                  onClick={() => handleFooterLinkClick('For Practitioners')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  For Practitioners
                </Link>
              </li>
              <li>
                <Link
                  to='/learn/lawyers'
                  onClick={() => handleFooterLinkClick('Learn for Lawyers')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Learn for Lawyers
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className='text-white font-semibold mb-4' style={sectionHeadingStyle}>
              Tools
            </h4>
            <ul className='space-y-2'>
              <li>
                <Link
                  to='/check'
                  onClick={() => handleFooterLinkClick('Harbor Checks')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Harbor Checks
                </Link>
              </li>
              <li>
                <Link
                  to='/parenting-plan'
                  onClick={() => handleFooterLinkClick('Parenting Plan Builder')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Parenting Plan Builder
                </Link>
              </li>
              <li>
                <Link
                  to='/child-support-calculator'
                  onClick={() => handleFooterLinkClick('Child Support Calculator')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Child Support Calculator
                </Link>
              </li>
              <li>
                <Link
                  to='/coparenting-app-cost-calculator'
                  onClick={() => handleFooterLinkClick('App Cost Calculator')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  App Cost Calculator
                </Link>
              </li>
              <li>
                <Link
                  to='/court-order-language'
                  onClick={() => handleFooterLinkClick('Court Order Language')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Court Order Language
                </Link>
              </li>
              <li>
                <Link
                  to='/custody-schedule-app'
                  onClick={() => handleFooterLinkClick('Custody Schedule App')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Custody Schedule App
                </Link>
              </li>
              <li>
                <Link
                  to='/visitation-tracker'
                  onClick={() => handleFooterLinkClick('Visitation Tracker')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Visitation Tracker
                </Link>
              </li>
              <li>
                <Link
                  to='/coparent-money'
                  onClick={() => handleFooterLinkClick('Coparent Money')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Coparent Money
                </Link>
              </li>
              <li>
                <Link
                  to='/court-ready-records'
                  onClick={() => handleFooterLinkClick('Court-Ready Records')}
                  className={linkClassName}
                  style={linkStyle}
                >
                  Court-Ready Records
                </Link>
              </li>
              {divorceEnabled ? (
                <li>
                  <Link
                    to='/divorce'
                    onClick={() => handleFooterLinkClick('Harbor Divorce')}
                    className={linkClassName}
                    style={linkStyle}
                  >
                    Harbor Divorce
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>

          <div>
            <h4
              className='text-white font-semibold mb-4'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              Comparisons
            </h4>
            <ul className='space-y-2'>
              {COMPARISON_LINKS.map((link) => (
                <li key={link.competitor}>
                  <Link
                    to='/compare/$competitor'
                    params={{ competitor: link.competitor }}
                    onClick={() => handleFooterLinkClick(link.label)}
                    className='text-white/70 hover:text-white transition-colors'
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              className='text-white font-semibold mb-4'
              style={{
                fontFamily:
                  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 600,
              }}
            >
              Legal & Support
            </h4>
            <ul className='space-y-2'>
              <li>
                <Link
                  to='/privacy-policy'
                  onClick={() => handleFooterLinkClick('Privacy Policy')}
                  className='text-white/70 hover:text-white transition-colors'
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to='/terms-of-service'
                  onClick={() => handleFooterLinkClick('Terms of Service')}
                  className='text-white/70 hover:text-white transition-colors'
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to='/support'
                  onClick={() => handleFooterLinkClick('Support')}
                  className='text-white/70 hover:text-white transition-colors'
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Support
                </Link>
              </li>
              <li>
                <Link
                  to='/fee-waivers'
                  onClick={() => handleFooterLinkClick('Fee Waivers')}
                  className='text-white/70 hover:text-white transition-colors'
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Fee Waivers
                </Link>
              </li>
              <li>
                <Link
                  to='/courts'
                  onClick={() => handleFooterLinkClick('Courts')}
                  className='text-white/70 hover:text-white transition-colors'
                  style={{
                    fontFamily:
                      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                    fontWeight: 400,
                  }}
                >
                  Courts
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className='border-t border-white/20 pt-8'>
          <p
            className='text-white/50 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
              fontWeight: 400,
            }}
          >
            © {new Date().getFullYear()} Harbor. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
